/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button, Loader } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { useAPIData } from "~/hooks/useAPIData";
import { ServiceCmtSmall } from "~/models";
import { generateActionSuccessText, useAxios } from "~/tools";
import { ServiceViewCtx } from "./ServiceViewCtx";

export default function () {
  const { service, refetch } = React.useContext(ServiceViewCtx);

  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const [commitments, isLoading] = useAPIData<ServiceCmtSmall[]>(
    "/search/services",
    (data) => data.searchResults,
    []
  );

  const methods = useForm();

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: { serviceCommitmentId: string }) => {
    setLoading(true);
    try {
      await Axios().post(`/services/${service.id}/commitments`, values);
      showSuccess(
        generateActionSuccessText("Service commitment")()("attached")()
      );
      setLoading(false);
      setIsSidebarOpen(false);

      return refetch();
    } catch (e) {
      showError(e.response);
      setLoading(false);
    }
  };

  const options = commitments
    .filter((cmt) => service.serviceCommitments.every((c) => c.id !== cmt.id))
    .map((cmt) => ({ label: cmt.name, value: cmt.id }));

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        size="md"
        css={{ marginLeft: "auto", marginBottom: "1rem" }}
        icon="plus"
      >
        Attach commitment
      </Button>

      <SidebarForm
        title="Attach service commitment"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        {isLoading ? (
          <div style={{ marginBottom: "1.5rem" }}>
            <Loader inContent />
          </div>
        ) : (
          <SidebarDropdown
            name="serviceCommitmentId"
            label="Name"
            options={options}
            required
          />
        )}

        <SidebarFormButtons
          submitText="Attach"
          submitEnabled={!!methods.watch("serviceCommitmentId")}
          submitLoading={loading}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}
